import { useEffect, useState, lazy, Suspense } from "react";
import HeaderBar from "@/components/HeaderBar";
import BottomNav from "@/components/BottomNav";
import AuthScreen from "@/components/views/AuthScreen";
import ViewRouter from "@/components/ViewRouter";
import { useAuth } from "@/hooks/useAuth";
import { useAppStore } from "@/stores/useAppStore";
import { useShallow } from "zustand/react/shallow";
import { supabase } from "@/integrations/supabase/client";
import SplashScreen from "@/components/SplashScreen";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import FluidBackground from "@/components/FluidBackground";

// Lazy load heavy onboarding views
const ClientOnboardingView = lazy(() => import("@/components/views/ClientOnboardingView"));
const MemberOnboardingView = lazy(() => import("@/components/views/MemberOnboardingView"));

const Index = () => {
  const { user, isAAL2Verified, loading: authLoading } = useAuth();
  const [initDone, setInitDone] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsMemberOnboarding, setNeedsMemberOnboarding] = useState(false);
  const [statusText, setStatusText] = useState("Checking session...");

  // Provider 2FA Gate State
  const [showProviderGate, setShowProviderGate] = useState(false);
  const [providerMfaFactorId, setProviderMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [pendingAutoBoot, setPendingAutoBoot] = useState(false);

  const {
    currentView,
    userRole,
    roleLoaded,
    profileName,
    handleTabNavigate,
    setUserRole,
    setRoleLoaded,
    setProfileName,
    navigateTo,
    activeProviderChat,
  } = useAppStore(
    useShallow((state) => ({
      currentView: state.currentView,
      userRole: state.userRole,
      roleLoaded: state.roleLoaded,
      profileName: state.profileName,
      handleTabNavigate: state.handleTabNavigate,
      setUserRole: state.setUserRole,
      setRoleLoaded: state.setRoleLoaded,
      setProfileName: state.setProfileName,
      navigateTo: state.navigateTo,
      activeProviderChat: state.activeProviderChat,
    }))
  );

  // Single consolidated init via RPC — one database trip
  useEffect(() => {
    if (!user) {
      if (!authLoading) {
        setInitDone(true);
      }
      return;
    }

    const init = async () => {
      setStatusText("Preparing your dashboard...");
      const { data: state, error } = await supabase.rpc("get_user_init_state" as any, {
        _user_id: user.id,
      });

      if (error || !state) {
        console.error("Init state error object:", JSON.stringify(error, null, 2));
        console.error("State value:", state);
        setInitDone(true);
        setRoleLoaded(true);
        return;
      }

      const s = state as any;

      if (s.full_name) {
        setProfileName(s.full_name);
      }

      // Check onboarding
      if (!s.onboarding_completed) {
        if (s.has_team_role && (!s.full_name || s.full_name.trim() === "")) {
          setNeedsMemberOnboarding(true);
        } else {
          setNeedsOnboarding(true);
        }
      } else {
        setNeedsOnboarding(false);
        setNeedsMemberOnboarding(false);
      }

      // Check role
      if (!roleLoaded) {
        // Only auto-switch to provider mode if they have the role AND a business/team association
        if (s.is_provider && (s.has_business || s.has_team_role)) {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const enrolled = factors?.totp?.[0];
          if (enrolled) {
            setProviderMfaFactorId(enrolled.id);
            setPendingAutoBoot(true);
            setShowProviderGate(true);
          } else {
            setUserRole("provider");
            handleTabNavigate("hub");
          }
        }
        setRoleLoaded(true);
      }

      // Delay slightly for smooth transition
      setTimeout(() => setInitDone(true), 400);
    };

    init();
  }, [user, isAAL2Verified, authLoading]);

  // Unified Loading Experience
  if (authLoading || (user && !initDone)) {
    return <SplashScreen statusText={statusText} />;
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto h-[100dvh] overflow-hidden relative shadow-2xl bg-emerald-base">
        <AuthScreen />
      </div>
    );
  }

  // Show member onboarding for invited staff
  if (needsMemberOnboarding) {
    return (
      <div className="max-w-md mx-auto h-[100dvh] overflow-hidden relative shadow-2xl bg-emerald-base">
        <ErrorBoundary>
          <Suspense fallback={<SplashScreen statusText="Loading profile setup..." />}>
            <MemberOnboardingView
              userId={user!.id}
              onComplete={() => {
                setNeedsMemberOnboarding(false);
                setUserRole("provider");
                handleTabNavigate("hub");
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }

  // Show onboarding if not completed
  if (needsOnboarding) {
    return (
      <div className="max-w-md mx-auto h-[100dvh] overflow-hidden relative shadow-2xl bg-emerald-base">
        <ErrorBoundary>
          <Suspense fallback={<SplashScreen statusText="Loading onboarding..." />}>
            <ClientOnboardingView
              onComplete={() => {
                setNeedsOnboarding(false);
                handleTabNavigate("home");
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }

  const handleToggleRole = async () => {
    if (userRole === "consumer") {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const enrolled = factors?.totp?.[0];
      if (enrolled) {
        setProviderMfaFactorId(enrolled.id);
        setPendingAutoBoot(false);
        setShowProviderGate(true);
        return;
      }

      const { data } = await supabase
        .from("businesses")
        .select("id")
        .eq("owner_id", user!.id)
        .maybeSingle();
      if (data) {
        setUserRole("provider");
        handleTabNavigate("hub");
      } else {
        navigateTo("business-onboarding");
      }
    } else {
      setUserRole("consumer");
      handleTabNavigate("home");
    }
  };

  const handleVerifyProviderMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerMfaFactorId || mfaCode.length < 6) return;
    setIsVerifyingMfa(true);

    try {
      const challenge = await supabase.auth.mfa.challenge({ factorId: providerMfaFactorId });
      if (challenge.error) throw challenge.error;

      const verify = await supabase.auth.mfa.verify({
        factorId: providerMfaFactorId,
        challengeId: challenge.data.id,
        code: mfaCode,
      });
      if (verify.error) throw verify.error;

      setShowProviderGate(false);
      setMfaCode("");
      toast.success("Identity Verified!");

      if (pendingAutoBoot) {
        setUserRole("provider");
        handleTabNavigate("hub");
      } else {
        const { data } = await supabase
          .from("businesses")
          .select("id")
          .eq("owner_id", user!.id)
          .maybeSingle();
        if (data) {
          setUserRole("provider");
          handleTabNavigate("hub");
        } else {
          navigateTo("business-onboarding");
        }
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Invalid Authenticator code.");
    } finally {
      setIsVerifyingMfa(false);
    }
  };

  const isFullscreenView = [
    "provider-profile", "chat", "system-notifications", "business-onboarding",
    "admin-verification", "all-providers", "job-history", "upcoming-jobs",
  ].includes(currentView);

  const showHeader = !isFullscreenView;
  const showBottomNav = !isFullscreenView && !activeProviderChat;
  const displayName = profileName || user?.email?.split("@")[0] || "";

  return (
    <div className="max-w-md mx-auto h-[100dvh] flex flex-col overflow-hidden relative shadow-2xl bg-emerald-base">
      <FluidBackground />
      {showHeader && <HeaderBar userName={displayName} isProvider={userRole === "provider"} />}
      <ViewRouter onToggleRole={handleToggleRole} />
      {showBottomNav && (
        <BottomNav currentView={currentView} onNavigate={handleTabNavigate} userRole={userRole} />
      )}

      {/* Business Mode 2FA Gate */}
      <Dialog open={showProviderGate} onOpenChange={setShowProviderGate}>
        <DialogContent className="max-w-[340px] rounded-[20px] p-6 glass-frozen" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg font-bold text-center leading-tight text-foreground">Business Access</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-2">
            <p className="text-[13px] text-center text-muted-foreground leading-relaxed">
              Two-Factor Authentication is actively protecting your Business Hub. Please enter your code.
            </p>
            <form onSubmit={handleVerifyProviderMFA} className="w-full space-y-5">
              <div className="flex justify-center w-full">
                <InputOTP
                  maxLength={6}
                  value={mfaCode}
                  onChange={(value) => setMfaCode(value.replace(/\D/g, "").slice(0, 6))}
                  autoFocus
                >
                  <InputOTPGroup className="gap-2 w-full justify-center">
                    <InputOTPSlot index={0} className="w-10 h-12 text-xl font-bold border rounded-lg" style={{ background: "var(--input-bg)", borderColor: "var(--input-border)" }} />
                    <InputOTPSlot index={1} className="w-10 h-12 text-xl font-bold border rounded-lg" style={{ background: "var(--input-bg)", borderColor: "var(--input-border)" }} />
                    <InputOTPSlot index={2} className="w-10 h-12 text-xl font-bold border rounded-lg" style={{ background: "var(--input-bg)", borderColor: "var(--input-border)" }} />
                    <InputOTPSlot index={3} className="w-10 h-12 text-xl font-bold border rounded-lg" style={{ background: "var(--input-bg)", borderColor: "var(--input-border)" }} />
                    <InputOTPSlot index={4} className="w-10 h-12 text-xl font-bold border rounded-lg" style={{ background: "var(--input-bg)", borderColor: "var(--input-border)" }} />
                    <InputOTPSlot index={5} className="w-10 h-12 text-xl font-bold border rounded-lg" style={{ background: "var(--input-bg)", borderColor: "var(--input-border)" }} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="submit"
                disabled={isVerifyingMfa || mfaCode.length !== 6}
                className="w-full h-11 rounded-full font-medium bg-emerald-secondary text-white hover:brightness-110 active:scale-[0.98] transition-all border border-white/12 shadow-[var(--btn-primary-shadow)]"
              >
                {isVerifyingMfa ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Enter"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;
